import React, { useState, useEffect } from 'react';
import { recruiterAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { User, Phone, CheckCircle2, Pencil } from 'lucide-react';
import ChangePasswordCard from '@/components/shared/ChangePasswordCard';
import EmailVerifyCard from '@/components/shared/EmailVerifyCard';
import WhatsAppPhoneVerify from '@/components/shared/WhatsAppPhoneVerify';
import BiometricSettingCard from '@/components/shared/BiometricSettingCard';

export default function RecruiterSettings() {
  const { user, loadUser } = useAuthStore();
  const [profileForm, setProfileForm] = useState({ name: '', position: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Phone-change state
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [newPhone, setNewPhone] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const p = await recruiterAPI.getProfile();
        setProfileForm({ name: p.name, position: p.position });
      } catch { /* profile load best-effort */ }
      setLoaded(true);
    };
    load();
  }, [user]);

  const updateProfile = async (e) => {
    e.preventDefault();
    try { await recruiterAPI.updateProfile(profileForm); setProfileMsg('Profile updated!'); }
    catch (err) { setProfileMsg('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div data-testid="recruiter-settings">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Settings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Manage your account</p>
      <div className="space-y-6">
        <EmailVerifyCard testIdPrefix="recruiter" />

        <BiometricSettingCard />

        {/* Phone Number — WhatsApp-verified, changeable via OTP */}
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid="recruiter-phone-card">
          <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-1"><Phone className="w-4 h-4" /> Phone Number</h3>
          <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">Your phone is the login identifier for WhatsApp login and alerts. Changing it requires a fresh WhatsApp OTP.</p>

          {!phoneEditing ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" data-testid="recruiter-phone-verified-badge">
                <CheckCircle2 className="w-3.5 h-3.5" /> {user?.phone || 'Not set'}
              </span>
              <button
                onClick={() => { setPhoneEditing(true); setNewPhone(''); }}
                className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200"
                data-testid="recruiter-change-phone-btn"
              >
                <Pencil className="w-3 h-3" /> Change phone
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="tel"
                placeholder="+919876543210"
                value={newPhone}
                onChange={e => setNewPhone(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600"
                data-testid="recruiter-new-phone-input"
              />
              <WhatsAppPhoneVerify
                phone={newPhone}
                mode="change-phone"
                testIdPrefix="recruiter-wa"
                onVerified={async () => {
                  setPhoneEditing(false);
                  setNewPhone('');
                  await loadUser();
                }}
              />
              <button
                onClick={() => { setPhoneEditing(false); setNewPhone(''); }}
                className="text-xs text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                data-testid="recruiter-cancel-change-phone-btn"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Edit Profile */}
        {loaded && (
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
            <h3 className="text-slate-900 dark:text-white font-medium text-sm flex items-center gap-2 mb-4"><User className="w-4 h-4" /> Edit Profile</h3>
            {profileMsg && <p className={`text-sm mb-3 ${profileMsg.startsWith('Error') ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{profileMsg}</p>}
            <form onSubmit={updateProfile} className="space-y-3">
              <input placeholder="Name" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" />
              <input placeholder="Position" value={profileForm.position} onChange={e => setProfileForm({...profileForm, position: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" />
              <button type="submit" className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm">Update</button>
            </form>
          </div>
        )}

        {/* Change Password */}
        <ChangePasswordCard testIdPrefix="recruiter-pw" />
      </div>
    </div>
  );
}

